import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { MessageCircle, Phone } from "lucide-react";
import FestivalBanner from "@/components/FestivalBanner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Gallery from "@/pages/Gallery";
import About from "@/pages/About";
import MenuBuilder from "@/pages/MenuBuilder";
import SharedMenu from "@/pages/SharedMenu";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

const whatsappUrl = "https://wa.me/919923385867?text=Hello%20Mayuresh%20Caterers%2C%20I%20would%20like%20to%20enquire%20about%20catering%20for%20my%20event.";

function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      <main className="pt-[72px]"><FestivalBanner />{children}</main>
      <Footer />
      <div className="fixed right-5 bottom-5 z-40 flex flex-col gap-3" aria-label="Quick contact">
        <a href={whatsappUrl} target="_blank" rel="noreferrer" aria-label="WhatsApp Mayuresh Caterers"
          className="w-12 h-12 rounded-full bg-[#1A1A1A] text-[#C9A66B] border border-[#C9A66B]/50 shadow-xl flex items-center justify-center hover:scale-105 transition-transform">
          <MessageCircle size={22} />
        </a>
        <a href="tel:+919923385867" aria-label="Call Mayuresh Caterers"
          className="w-12 h-12 rounded-full bg-[#C9A66B] text-[#1A1A1A] shadow-xl flex items-center justify-center hover:scale-105 transition-transform">
          <Phone size={20} />
        </a>
      </div>
      <div className="fixed bottom-0 left-0 right-0 z-30 md:hidden p-3 bg-[#1A1A1A]/95 backdrop-blur-sm border-t border-white/10">
        <div className="grid grid-cols-2 gap-2">
          <a href={whatsappUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-[#C9A66B] text-[#1A1A1A] rounded-sm py-3 text-sm font-semibold"><MessageCircle size={17} /> WhatsApp</a>
          <a href="tel:+919923385867" className="flex items-center justify-center gap-2 border border-[#C9A66B] text-[#E4CE9E] rounded-sm py-3 text-sm font-semibold"><Phone size={17} /> Call Now</a>
        </div>
      </div>
    </>
  );
}

function ProtectedRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking || user === null)
    return <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A] text-[#C9A66B]">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <SiteProvider>
        <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
              <Route path="/gallery" element={<PublicLayout><Gallery /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/build" element={<PublicLayout><MenuBuilder /></PublicLayout>} />
              <Route path="/menu/:id" element={<PublicLayout><SharedMenu /></PublicLayout>} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </AuthProvider>
        </LanguageProvider>
      </SiteProvider>
    </div>
  );
}

export default App;
